import { isAdminUserType, isMotoristaUserType } from '@/utils/userRoles';

export type ChecklistCreationMode = 'self' | 'assign';

export interface ChecklistAssignmentCandidate {
  id: string;
  empresa_id?: string | null;
  user_type?: string | null;
  name?: string | null;
  email?: string | null;
}

export interface ChecklistTransferAuthorizationInput {
  actorId?: string | null; actorCompanyId?: string | null; actorUserType?: string | null;
  ownerId?: string | null; checklistCompanyId?: string | null;
}

export const canAssignChecklist = (userType?: string | null): boolean =>
  isAdminUserType(userType);

export const getAssignableChecklistDrivers = (
  users: ChecklistAssignmentCandidate[],
  companyId: string,
): ChecklistAssignmentCandidate[] => {
  const normalizedCompanyId = companyId.trim();
  if (!normalizedCompanyId) return [];

  return users
    .filter((user) => (
      typeof user.id === 'string'
      && user.id.trim().length > 0
      && String(user.empresa_id ?? '').trim() === normalizedCompanyId
      && isMotoristaUserType(user.user_type)
    ))
    .sort((left, right) => {
      const leftLabel = left.name?.trim() || left.email?.trim() || left.id;
      const rightLabel = right.name?.trim() || right.email?.trim() || right.id;
      return leftLabel.localeCompare(rightLabel, 'pt-BR', { sensitivity: 'base' });
    });
};

/** Mantém a ação coerente com o papel local; a RPC continua sendo a autoridade final. */
export const canTransferChecklist = ({
  actorId, actorCompanyId, actorUserType, ownerId, checklistCompanyId,
}: ChecklistTransferAuthorizationInput): boolean => {
  const normalizedActorId = actorId?.trim() || '';
  const normalizedActorCompanyId = actorCompanyId?.trim() || '';
  const normalizedOwnerId = ownerId?.trim() || '';
  const normalizedChecklistCompanyId = checklistCompanyId?.trim() || '';

  if (!normalizedActorId || !normalizedActorCompanyId || !normalizedOwnerId
    || !normalizedChecklistCompanyId
    || normalizedActorCompanyId !== normalizedChecklistCompanyId) return false;

  if (isAdminUserType(actorUserType)) return true;
  return isMotoristaUserType(actorUserType) && normalizedActorId === normalizedOwnerId;
};

export const getTransferableChecklistRecipients = (
  users: ChecklistAssignmentCandidate[],
  companyId: string,
  currentOwnerId?: string | null,
): ChecklistAssignmentCandidate[] => {
  const normalizedCompanyId = companyId.trim();
  const normalizedOwnerId = currentOwnerId?.trim() || '';
  if (!normalizedCompanyId) return [];

  return users
    .filter((user) => (
      typeof user.id === 'string'
      && user.id.trim().length > 0
      && String(user.empresa_id ?? '').trim() === normalizedCompanyId
      && (isMotoristaUserType(user.user_type) || isAdminUserType(user.user_type))
      && user.id.trim() !== normalizedOwnerId
    ))
    .sort((left, right) => {
      const leftLabel = left.name?.trim() || left.email?.trim() || left.id;
      const rightLabel = right.name?.trim() || right.email?.trim() || right.id;
      return leftLabel.localeCompare(rightLabel, 'pt-BR', { sensitivity: 'base' });
    });
};

export const getChecklistCreationUserId = ({
  mode,
  currentUserId,
  targetUserId,
}: {
  mode: ChecklistCreationMode;
  currentUserId?: string | null;
  targetUserId?: string | null;
}): string | null => {
  const selectedUserId = mode === 'assign' ? targetUserId : currentUserId;
  const normalizedUserId = selectedUserId?.trim() || '';
  return normalizedUserId || null;
};
